/**
 * useMyHealthReports — 客户已发布健康报告 hook（Issue #198）。
 *
 * 红线：
 *   - 仅展示后端已 published 的行（state === 'published'）。
 *   - 客户端不构造 tenantId / orgId / customerId / serviceUserId 用于鉴权；
 *     filter 仅作为可选服务用户过滤项，由后端校验归属。
 *   - 不展示 reviewedBy / reviewNote / sourceProposalReviewId 等内部字段。
 *   - 不在 UI 层尝试推断 AI 草稿是否可见；只信任后端响应。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiComponents } from '@jinzhuan/api-client';
import {
  createCustomerMeApiClient,
  refreshCustomerSession,
} from './customerSession';
import { classifyMeFetchError, type MeFetchError } from './classify';

export type HealthReportPublicationMeDto =
  ApiComponents['schemas']['HealthReportPublicationMeDto'];
export type HealthReportMetricDto =
  ApiComponents['schemas']['HealthReportMetricDto'];
export type HealthReportPublicationKind =
  HealthReportPublicationMeDto['reportKind'];

export interface UseMyHealthReportsOptions {
  reportKind?: HealthReportPublicationKind;
  serviceUserId?: string;
}

interface UseMyHealthReportsResult {
  publications: HealthReportPublicationMeDto[];
  loading: boolean;
  error: MeFetchError | null;
  refetch: () => void;
}

/**
 * Filter visible-to-customer publications. The backend should only return
 * `published` rows on this endpoint, but the frontend filters again as
 * defence-in-depth: if a draft / reviewed / retracted row ever leaks through
 * (e.g. a wrong scope, a bug in a future endpoint version), it must not
 * surface in the customer UI.
 */
export function filterCustomerVisible(
  rows: readonly HealthReportPublicationMeDto[],
): HealthReportPublicationMeDto[] {
  return rows.filter((row) => row.reviewState === 'published');
}

export function useMyHealthReports(
  options?: UseMyHealthReportsOptions,
): UseMyHealthReportsResult {
  const [publications, setPublications] = useState<HealthReportPublicationMeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<MeFetchError | null>(null);
  const [tick, setTick] = useState(0);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const refetch = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      const query: Record<string, string> = {};
      if (optionsRef.current?.reportKind) {
        query.reportKind = optionsRef.current.reportKind;
      }
      if (optionsRef.current?.serviceUserId) {
        query.serviceUserId = optionsRef.current.serviceUserId;
      }
      try {
        const apiClient = createCustomerMeApiClient();
        const result = await apiClient.GET('/api/v1/me/health-reports', {
          params: { query },
        });
        if (cancelled) return;
        setPublications(filterCustomerVisible(result.data ?? []));
      } catch (err: unknown) {
        if (cancelled) return;
        const classified = classifyMeFetchError(err);
        if (classified.kind === 'session_expired') {
          const refreshed = await refreshCustomerSession();
          if (cancelled) return;
          if (refreshed) {
            try {
              const apiClient = createCustomerMeApiClient();
              const retry = await apiClient.GET('/api/v1/me/health-reports', {
                params: { query },
              });
              setPublications(filterCustomerVisible(retry.data ?? []));
              setError(null);
              return;
            } catch {
              setError(classified);
              return;
            }
          }
        }
        setError(classified);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { publications, loading, error, refetch };
}
